/**
 * CameraSchedulesBlade — slide-out capture-schedule manager for one camera.
 *
 * The schedule surface for the compact camera tile: a DashboardBlade drawer
 * wrapping the shared CameraScheduleList so the relay-grid camera card stays
 * image-first at every breakpoint.
 *
 * Contains:
 * - CameraSchedulesBlade — the drawer (default export)
 */
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CameraScheduleList from '@sophocles/views/sites/components/SiteDashboard/CameraScheduleList';
import DashboardBlade from '@sophocles/views/sites/components/SiteDashboard/DashboardBlade';

const BLADE_WIDTH = { xs: '100%', sm: 480 };

export default function CameraSchedulesBlade({ siteId, open, onClose, camera, isVantage, canAddSchedules }) {
  const { t } = useTranslation();

  const cameraNumber = camera ? camera.camera_number : null;
  const label = isVantage ? t('sites.screenshot') : `${t('sites.camera')} ${cameraNumber ?? ''}`.trim();

  return (
    <DashboardBlade open={open} onClose={onClose} width={BLADE_WIDTH} title={t('general.schedules')} subtitle={label}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 'var(--page-gap)' }}>
        <CameraScheduleList siteId={siteId} camera={camera} canAddSchedules={canAddSchedules} />
      </Box>
    </DashboardBlade>
  );
}

CameraSchedulesBlade.propTypes = {
  /** Site id — required to route write calls to the odl-app endpoint. */
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  /** Plain-JS camera object (null when the blade is closed). */
  camera: PropTypes.object,
  isVantage: PropTypes.bool,
  canAddSchedules: PropTypes.bool,
};
